/**
 * Shared Chatbot Zod Validation Schemas
 * Matches backend validation exactly for consistency.
 * Used by both frontend (code-editor) and backend (API validation).
 */
import * as z from 'zod';

import { metaAgentConfigSchema } from '../metaAgent';

import {
  isJourneyConfigPath,
  isPyFlowConfigPath,
  isFlowBotJsonPath,
} from '../copilot';

// ============================================
// Function Parameters & Execution Details
// ============================================

// Schema for parameter properties
const parameterPropertiesSchema = z.record(z.unknown());

// Schema for function parameters
export const functionParametersSchema = z.object({
  type: z.string(),
  properties: parameterPropertiesSchema,
  required: z.array(z.string()).optional(),
  additionalProperties: z.boolean().default(false),
});

// HTTP Methods
const httpMethodEnum = z.enum(['GET', 'POST', 'PUT', 'DELETE', 'PATCH']);

// Schema for API call execution details
export const apiCallExecutionDetailsSchema = z.object({
  type: z.literal('api_call'),
  apiUrl: z.string().url('API URL must be a valid URL'),
  method: httpMethodEnum.default('POST'),
  headers: z.record(z.string()).default({}),
});

// Schema for Lambda function execution details
export const lambdaFunctionExecutionDetailsSchema = z.object({
  type: z.enum(['lambda_function', 'unified_function_event_handlers']),
});

// Schema for execution details
export const executionDetailsSchema = z.union([
  apiCallExecutionDetailsSchema,
  lambdaFunctionExecutionDetailsSchema,
  z.null(),
]);

// ============================================
// Reasoning & Verbosity (for Chatbot models)
// Supports all providers: OpenAI GPT-5.x, Cerebras GPT-OSS, Groq GPT-OSS
// ============================================

export const chatbotVerbositySchema = z.enum(['low', 'medium', 'high']);

export const chatbotReasoningEffortSchema = z.enum([
  'default',
  'none',
  'minimal',
  'low',
  'medium',
  'high',
  'xhigh',
]);

export type ChatbotVerbosity = z.infer<typeof chatbotVerbositySchema>;
export type ChatbotReasoningEffort = z.infer<
  typeof chatbotReasoningEffortSchema
>;

export interface ChatbotReasoningConfig {
  options: ChatbotReasoningEffort[];
  defaultValue: ChatbotReasoningEffort;
}

// Efforts each model accepts — one source for the settings dropdown and the API call; OpenAI 400s on a value the model dropped.
export const getModelReasoningConfig = (
  modelId: string,
): ChatbotReasoningConfig | null => {
  // OpenAI GPT-5 original family — the only builds that still take `minimal`.
  if (
    modelId === 'gpt-5' ||
    modelId === 'gpt-5-mini' ||
    modelId === 'gpt-5-nano'
  ) {
    return {
      options: ['minimal', 'low', 'medium', 'high'],
      defaultValue: 'medium',
    };
  }

  // OpenAI GPT-5.1 — dropped `minimal`, no `xhigh` yet.
  if (modelId === 'gpt-5.1') {
    return { options: ['none', 'low', 'medium', 'high'], defaultValue: 'none' };
  }

  // OpenAI GPT-5.1 Codex Max / 5.2 / 5.4 / 5.5 / 5.6 / 6 — `none`..`xhigh`, never `minimal`.
  if (
    modelId === 'gpt-5.1-codex-max' ||
    modelId === 'gpt-5.2' ||
    modelId === 'gpt-5.4' ||
    modelId === 'gpt-5.4-mini' ||
    modelId === 'gpt-5.5' ||
    modelId.startsWith('gpt-5.6') ||
    modelId.startsWith('gpt-6')
  ) {
    return {
      options: ['none', 'low', 'medium', 'high', 'xhigh'],
      defaultValue: 'none',
    };
  }

  // Vertex adapter maps these to thinkingBudget tiers; 'default' leaves Gemini's own.
  if (modelId.startsWith('google/gemini')) {
    return {
      options: ['default', 'none', 'low', 'medium', 'high'],
      defaultValue: 'none',
    };
  }

  if (modelId === 'cerebras/gpt-oss-120b') {
    return { options: ['low', 'medium', 'high'], defaultValue: 'medium' };
  }

  if (
    modelId === 'groq/openai/gpt-oss-120b' ||
    modelId === 'groq/openai/gpt-oss-20b'
  ) {
    return { options: ['low', 'medium', 'high'], defaultValue: 'medium' };
  }

  // Groq Qwen3 — 'default' is thinking ON, 'none' is thinking OFF.
  if (modelId === 'groq/qwen/qwen3-32b') {
    return { options: ['default', 'none'], defaultValue: 'default' };
  }

  return null;
};

// Effort to put on the wire, or undefined — a value the model rejects is dropped so it degrades to the model default instead of 400ing.
export const sanitizeChatbotReasoningEffort = (
  modelId: string,
  effort?: ChatbotReasoningEffort | null,
): Exclude<ChatbotReasoningEffort, 'default'> | undefined => {
  if (!effort || effort === 'default') return undefined;
  const config = getModelReasoningConfig(modelId);
  if (!config) return undefined;
  return config.options.includes(effort) ? effort : undefined;
};

/**
 * The newest OpenAI families (gpt-5.x, gpt-6.x, whatever follows) take
 * `reasoning_effort` and `verbosity`, reject any temperature but 1, and sit
 * behind the Azure `/v1` endpoint with a Bearer header; only gpt-4.x keeps the
 * old shape. Matching the OLD family is deliberate — a `startsWith('gpt-5')`
 * check is exactly what let gpt-6 through with gpt-4 defaults. Realtime builds
 * are a different API and still take a temperature, so they stay out.
 */
export const isNewModelFamily = (modelId?: string | null): boolean =>
  !!modelId &&
  modelId.startsWith('gpt-') &&
  !modelId.startsWith('gpt-4') &&
  !modelId.includes('realtime');

/**
 * The `reasoning_effort` to send on Azure `/v1/chat/completions` for a request
 * that carries function tools. Azure 400s the tools + reasoning combination on
 * the newest families, and the cure differs per family (verified live
 * 2026-09-09): gpt-5.5/5.6 accept the call only with the field absent, while
 * gpt-6 accepts it only with an explicit `'none'` — omitting it there lets the
 * model's own default effort apply, which 400s exactly the same way. Requests
 * without tools are unaffected and keep the bot's configured effort.
 */
export const resolveChatCompletionsEffort = (
  modelId: string,
  hasTools: boolean,
  effort?: ChatbotReasoningEffort | null,
): Exclude<ChatbotReasoningEffort, 'default'> | undefined => {
  const safe = sanitizeChatbotReasoningEffort(modelId, effort);
  if (!hasTools) return safe;
  if (modelId.startsWith('gpt-6')) return 'none';
  if (modelId.startsWith('gpt-5.5') || modelId.startsWith('gpt-5.6'))
    return undefined;
  return safe;
};

// Chatbot kind — the SINGLE source for the `type` field across every chatbot schema
// (file / create / update, frontend + backend). Mirrors the ChatbotType entity enum.
export const chatbotTypeSchema = z.enum([
  'llm',
  'flow',
  'static_flowbot',
  'journey',
  'meta_ai',
]);
export type ChatbotKind = z.infer<typeof chatbotTypeSchema>;

// ============================================
// Environment Variables Schema
// ============================================

export const envSchema = z
  .record(z.string())
  .nullable()
  .default({})
  .optional()
  .transform(val => {
    // If val is not null (it could be an empty object from .default()),
    // convert it to a JSON string. Otherwise, return null.
    if (val && typeof val !== 'string') {
      return JSON.stringify(val);
    }
    return val; // This will be null
  });

// ============================================
// Timezone Schema (simplified - no moment dependency)
// ============================================

const TIMEZONE_ABBREVIATIONS: Record<string, string> = {
  IST: 'Asia/Kolkata',
  UTC: 'UTC',
  PST: 'America/Los_Angeles',
  EST: 'America/New_York',
  CET: 'Europe/Berlin',
  AEST: 'Australia/Sydney',
};

// Simple timezone validation (full validation with moment-tz is in backend)
export const timezoneSchema = z
  .string()
  .min(1)
  .refine(value => {
    const trimmed = value.trim();
    if (!trimmed) return false;
    // Allow IANA format (contains /) or known abbreviations
    if (trimmed.includes('/')) return true;
    return Boolean(TIMEZONE_ABBREVIATIONS[trimmed.toUpperCase()]);
  }, 'Invalid timezone identifier. Use a supported abbreviation (IST, UTC, PST, EST, CET, AEST) or a valid IANA timezone.');

// ============================================
// Function Definition Schema
// ============================================

export const functionDefinitionResponseSchema = z.object({
  id: z.string().optional(), // Optional for new functions
  type: z.string().optional().default('function'),
  name: z.string(),
  description: z.string(),
  parameters: functionParametersSchema,
  heltarParameters: functionParametersSchema.nullable().optional(),
  executionDetails: executionDetailsSchema.optional(),
  sourceCode: z.string().nullable().optional(),
  codeLanguage: z
    .enum(['python', 'javascript', 'java'])
    .default('python')
    .nullable()
    .optional(),
  env: z.record(z.string()).default({}).nullable().optional(),
  chatbot: z.unknown(),
  createdBy: z.enum(['copilot', 'human', 'api']).optional(),
  directReturn: z.boolean().default(false).optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

// ============================================
// Voice Bot Schemas
// ============================================

// Voice Bot TTS schema
export const voiceBotTTSSchema = z.object({
  provider: z.enum(['cartesia', 'elevenlabs', 'openai']).optional(),
  model: z.string().optional(),
  voiceId: z.string().optional(),
  speed: z.number().min(0.6).max(1.5).optional(),
  // ElevenLabs-specific params
  stability: z.number().min(0).max(1).optional(), // Voice stability (0.0-1.0)
  similarity_boost: z.number().min(0).max(1).optional(), // Similarity boost (0.0-1.0)
  streamingMode: z.enum(['sentence', 'word']).optional(), // ElevenLabs only: sentence (default) or word (low latency)
});

// Voice Bot STT schema
export const voiceBotSTTSchema = z.object({
  provider: z.enum(['deepgram', 'elevenlabs', 'assemblyai']).optional(),
  language: z.string().optional(),
  model: z.string().optional(),
  interimResults: z.boolean().optional(),
  // Deepgram-specific params
  smartFormat: z.boolean().optional(), // Smart formatting (capitalize, numbers) - DEFAULT FALSE for low latency
  punctuate: z.boolean().optional(), // Add punctuation - DEFAULT FALSE for low latency
  profanityFilter: z.boolean().optional(), // Filter profanity
  endpointing: z.number().min(10).max(5000).optional(), // Endpointing in ms (default 250, Deepgram min 10ms)
  // ElevenLabs-specific params
  includeTimestamps: z.boolean().optional(), // Word-level timestamps
  tagAudioEvents: z.boolean().optional(), // Tag events like (laughter) - Scribe v1 only
});

// Voice Bot VAD (Voice Activity Detection) schema
export const voiceBotVADSchema = z.object({
  stopSecs: z.number().min(0.05).max(2.0).optional(), // Time to wait after speech stops (default 0.2, Silero VAD)
  startSecs: z.number().min(0.01).max(1.0).optional(), // Time to wait before speech starts (default 0.15, Silero VAD)
  confidence: z.number().min(0).max(1).optional(), // VAD confidence threshold (default 0.65)
});

// Voice Bot Recovery/Watchdog schema
export const voiceBotRecoverySchema = z.object({
  watchdogInterval: z.number().min(1).max(30).optional(), // How often to check for stuck state (default 3.0s)
  stuckThreshold: z.number().min(3).max(60).optional(), // Time before considering bot stuck (default 7.0s)
  recoveryWait: z.number().min(0.5).max(10).optional(), // Time to wait before recovery (default 2.0s)
});

// Voice Bot Realtime turn detection schema
export const voiceBotRealtimeTurnDetectionSchema = z.object({
  type: z.enum(['server_vad', 'semantic_vad']).optional(),
  eagerness: z.enum(['low', 'medium', 'high', 'auto']).optional(), // Only for semantic_vad (default 'high' for fast response)
  threshold: z.number().min(0).max(1).optional(), // For server_vad (default 0.5, lower = faster)
  prefix_padding_ms: z.number().optional(), // Padding before speech (default 200ms)
  silence_duration_ms: z.number().optional(), // Silence duration to end turn (default 300ms)
  create_response: z.boolean().optional(),
  interrupt_response: z.boolean().optional(),
});

// Voice Bot Realtime noise reduction schema
export const voiceBotRealtimeNoiseReductionSchema = z.object({
  type: z.enum(['near_field', 'far_field']).optional(),
});

// Map from raw Gemini Live voice names (as Google ships them) to the Heltar
// Indian-name alias the rest of the system uses. Legacy chatbot configs may
// have the raw name embedded — preprocess converts them silently so the
// existing enum check passes. Lookup is case-insensitive so 'Kore', 'kore'
// and 'KORE' all map identically.
// Source of truth for the alias-to-Gemini mapping: GEMINI_REALTIME_VOICES in
// frontend/src/app/components/ChatbotComponent/constants.ts (previewUrl).
const GEMINI_RAW_TO_HELTAR_VOICE: Record<string, string> = {
  zephyr: 'priya',
  puck: 'kiara',
  charon: 'vikram',
  kore: 'meera',
  fenrir: 'jhanvi',
  leda: 'ananya',
  orus: 'karan',
  aoede: 'kavya',
  callirrhoe: 'simran',
  autonoe: 'isha',
  enceladus: 'aditya',
  iapetus: 'ravi',
  umbriel: 'sahil',
  algieba: 'deepak',
  despina: 'nisha',
  erinome: 'pooja',
  algenib: 'rajesh',
  rasalgethi: 'swati',
  laomedeia: 'riya',
  achernar: 'tara',
  alnilam: 'amit',
  schedar: 'sneha',
  gacrux: 'madhuri',
  pulcherrima: 'divya',
  achird: 'nikhil',
  zubenelgenubi: 'varun',
  vindemiatrix: 'anjali',
  sadachbia: 'rahul',
  sadaltager: 'ashok',
  sulafat: 'neha',
};

const normalizeRealtimeVoice = (val: unknown): unknown => {
  if (typeof val !== 'string') return val;
  const mapped = GEMINI_RAW_TO_HELTAR_VOICE[val.toLowerCase()];
  return mapped ?? val;
};

const realtimeVoiceEnum = z.enum([
  // OpenAI Realtime voices
  'alloy',
  'ash',
  'ballad',
  'coral',
  'echo',
  'sage',
  'shimmer',
  'verse',
  'marin',
  'cedar',
  // Heltar Voice (Gemini Live) - Indian names
  'priya',
  'kiara',
  'vikram',
  'meera',
  'jhanvi',
  'ananya',
  'karan',
  'kavya',
  'simran',
  'isha',
  'aditya',
  'ravi',
  'sahil',
  'deepak',
  'nisha',
  'pooja',
  'rajesh',
  'swati',
  'riya',
  'tara',
  'amit',
  'sneha',
  'madhuri',
  'divya',
  'nikhil',
  'varun',
  'anjali',
  'rahul',
  'ashok',
  'neha',
]);

// Voice Bot Realtime schema (used when model is gpt-realtime)
export const voiceBotRealtimeSchema = z.object({
  voice: z.preprocess(normalizeRealtimeVoice, realtimeVoiceEnum.optional()),
  turnDetection: voiceBotRealtimeTurnDetectionSchema.nullable().optional(),
  noiseReduction: voiceBotRealtimeNoiseReductionSchema.nullable().optional(),
});

// Valid builtin audio clip names for background noise
const builtinAudioClipNames = [
  'office_ambience',
  'crowded_room',
  'city_ambience',
  'forest_ambience',
  'keyboard_typing',
  'keyboard_typing2',
  'hold_music',
  'none',
] as const;

// Accepts either a builtin clip name or a valid URL
const audioSourceSchema = z
  .string()
  .refine(
    val =>
      builtinAudioClipNames.includes(
        val as (typeof builtinAudioClipNames)[number],
      ) || z.string().url().safeParse(val).success,
    { message: 'Must be a builtin clip name or a valid URL' },
  );

// Voice Bot Background Noise schema
export const voiceBotBackgroundNoiseSchema = z.object({
  enabled: z.boolean().optional(), // Default FALSE for low latency
  volume: z.number().min(0).max(1).optional(),
  useIntegerMath: z.boolean().optional(), // Use integer math for faster processing (default true)
  ambientSound: audioSourceSchema.optional(), // BuiltinAudioClip name or public audio URL
  thinkingSound: audioSourceSchema.optional(), // BuiltinAudioClip name or public audio URL
});

// Voice Bot Session schema (AgentSession settings - applies to both modes)
export const voiceBotSessionSchema = z.object({
  minEndpointingDelay: z.number().min(0).max(2).optional(), // Min wait before finalizing user speech (default 0.1 traditional, 0.0 realtime)
  maxEndpointingDelay: z.number().min(0.3).max(5).optional(), // Max wait before finalizing user speech (default 1.5)
  preemptiveGeneration: z.boolean().optional(), // Start generating before user finishes (default true)
  minInterruptionDuration: z.number().min(0.1).max(2).optional(), // Min speech to interrupt agent (default 0.3)
  minInterruptionWords: z.number().min(0).max(5).optional(), // Min transcribed words to interrupt agent — noise guard (default 1 for Deepgram, 0 otherwise)
});

// Voice Bot Silence Watchdog schema
export const voiceBotSilenceWatchdogSchema = z.object({
  enabled: z.boolean().optional(), // Default FALSE
  timeout: z.number().min(5).max(300).optional(), // Seconds before generating reply (default 30)
});

// Voice Bot Noise Cancellation schema (Krisp-powered, requires LiveKit Cloud)
export const voiceBotNoiseCancellationSchema = z.object({
  enabled: z.boolean().optional(), // Default FALSE
  model: z.enum(['NC', 'BVC', 'BVCTelephony']).optional(), // Default BVCTelephony
});

// Voice Bot Pronunciation Entry schema
export const voiceBotPronunciationEntrySchema = z.object({
  word: z.string().min(1).max(100),
  replacement: z.string().min(1).max(200),
  caseSensitive: z.boolean().optional(),
});

// Voice Bot Pronunciations schema (array of entries)
export const voiceBotPronunciationsSchema = z
  .array(voiceBotPronunciationEntrySchema)
  .optional();

// Voice Bot config schema
export const voiceBotSchema = z.object({
  provider: z.enum(['livekit', 'pipecat', 'elevenlabs']).optional(), // Voice bot provider
  elevenlabsAgentId: z.string().optional(), // ElevenLabs Conversational AI agent ID (required when provider is 'elevenlabs')
  elevenlabsAgentPublic: z.boolean().optional(), // true = public agent (no API key needed), false/undefined = private
  tts: voiceBotTTSSchema.optional(),
  stt: voiceBotSTTSchema.optional(),
  realtime: voiceBotRealtimeSchema.optional(), // Only used when model is 'gpt-realtime'
  backgroundNoise: voiceBotBackgroundNoiseSchema.optional(), // Background noise for realistic call center ambiance
  vad: voiceBotVADSchema.optional(), // VAD settings for latency optimization
  recovery: voiceBotRecoverySchema.optional(), // Recovery/watchdog settings
  session: voiceBotSessionSchema.optional(), // AgentSession settings (endpointing, preemptive generation, interruption)
  silenceWatchdog: voiceBotSilenceWatchdogSchema.optional(), // Generate reply after prolonged silence
  noiseCancellation: voiceBotNoiseCancellationSchema.optional(), // Krisp noise cancellation (LiveKit Cloud)
  pronunciations: voiceBotPronunciationsSchema, // Custom word pronunciations for TTS
  greetingInstruction: z.string().optional(), // Custom greeting instruction for voice bot
  prioritizeOpenAI: z.boolean().optional(), // Priority processing (service_tier="priority") — applied via Azure
});

// Inferred types from Zod schemas
export type VoiceBotTTSType = z.infer<typeof voiceBotTTSSchema>;
export type VoiceBotSTTType = z.infer<typeof voiceBotSTTSchema>;
export type VoiceBotVADType = z.infer<typeof voiceBotVADSchema>;
export type VoiceBotRecoveryType = z.infer<typeof voiceBotRecoverySchema>;
export type VoiceBotSessionType = z.infer<typeof voiceBotSessionSchema>;
export type VoiceBotRealtimeTurnDetectionType = z.infer<
  typeof voiceBotRealtimeTurnDetectionSchema
>;
export type VoiceBotRealtimeNoiseReductionType = z.infer<
  typeof voiceBotRealtimeNoiseReductionSchema
>;
export type VoiceBotRealtimeType = z.infer<typeof voiceBotRealtimeSchema>;
export type VoiceBotBackgroundNoiseType = z.infer<
  typeof voiceBotBackgroundNoiseSchema
>;
export type VoiceBotSilenceWatchdogType = z.infer<
  typeof voiceBotSilenceWatchdogSchema
>;
export type VoiceBotNoiseCancellationType = z.infer<
  typeof voiceBotNoiseCancellationSchema
>;
export type VoiceBotPronunciationEntryType = z.infer<
  typeof voiceBotPronunciationEntrySchema
>;
export type VoiceBotType = z.infer<typeof voiceBotSchema>;

// ============================================
// Chatbot Response Schema
// ============================================

export const chatbotResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  systemPrompt: z.string(),
  model: z.string(),
  hasTimeout: z.boolean(),
  maxTokens: z.number().optional(),
  temperature: z.number().optional(),
  reasoningEffort: chatbotReasoningEffortSchema.optional(),
  verbosity: chatbotVerbositySchema.optional(),
  contextLength: z.number().optional(),
  sendMsgType: z.array(z.string()).optional(),
  truncateMsg: z.boolean().optional(),
  showTypingIndicator: z.boolean().optional(),
  sendFirstMessage: z.boolean().optional(),
  minBotMsgDelay: z.number().optional(),
  timeoutDuration: z.number().nullable(),
  business: z.unknown().optional(),
  functionDefinitions: z.array(z.unknown()).optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  followUpPrompt: z.string().nullable().optional(),
  followUpLocation: z.enum(['system', 'assistant']).optional(),
  enableFollowUpType: z.boolean().optional(),
  includeTimestampInContext: z.boolean().optional(),
  contextTimestampTimezone: timezoneSchema.optional(),
});

// ============================================
// Create Chatbot Schema
// ============================================

// NOTE: POST /chatbots validates against the backend's own copy in middleware/validation/chatbot/chatbotSchemas.ts, not this one. Editing only this file changes nothing on the wire — keep the two in step.
export const createChatbotSchema = z.object({
  name: z.string(),
  systemPrompt: z.string().optional(),
  type: chatbotTypeSchema.optional(),
  flowDefinition: z.record(z.unknown()).optional(),
  hasTimeout: z.boolean().optional(),
  timeoutDuration: z.number().optional(),
  model: z.string().optional(),
  contextLength: z.number().optional(),
  // Without this the field is stripped before the controller sees it and the bot silently takes the column default — how new bots kept being born with 'minimal', which GPT-5.5/5.6 reject.
  reasoningEffort: chatbotReasoningEffortSchema.optional(),
  includeTimestampInContext: z.boolean().optional(),
  contextTimestampTimezone: timezoneSchema.optional(),
  createdBy: z.enum(['copilot', 'human', 'api']).optional(),
});

// ============================================
// Update Chatbot Schema
// ============================================

export const updateChatbotSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  systemPrompt: z.string().optional(),
  hasTimeout: z.boolean().optional(),
  timeoutDuration: z.number().nullable().optional(),
  model: z.string().optional(),
  contextLength: z.number().optional(),
  sendMsgType: z.array(z.string()).optional(),
  truncateMsg: z.boolean().optional(),
  showTypingIndicator: z.boolean().optional(),
  sendFirstMessage: z.boolean().optional(),
  temperature: z.number().optional(),
  maxTokens: z.number().optional(),
  reasoningEffort: chatbotReasoningEffortSchema.optional(),
  verbosity: chatbotVerbositySchema.optional(),
  minBotMsgDelay: z.number().optional(),
  functionDefinitions: z.array(functionDefinitionResponseSchema).optional(),
  maxConsecutiveNudges: z.number().optional(),
  maxBotMessagesPerSession: z.number().optional(),
  followUpPrompt: z.string().nullable().optional(),
  followUpLocation: z.enum(['system', 'assistant']).optional(),
  enableFollowUpType: z.boolean().default(false).optional(),
  includeTimestampInContext: z.boolean().optional(),
  contextTimestampTimezone: timezoneSchema.optional(),
  voiceBot: voiceBotSchema.optional(),
  type: chatbotTypeSchema.optional(),
  flowDefinition: z.record(z.unknown()).optional(),
  metaAgentConfig: metaAgentConfigSchema.nullable().optional(),
});

export type UpdateChatbotSchemaType = z.infer<typeof updateChatbotSchema>;

// ============================================
// Execute Functions Schema
// ============================================

export const executeFunctionsSchema = z.object({
  functionId: z.string(),
  parameters: parameterPropertiesSchema,
  sourceCode: z.string().optional(),
  env: z.record(z.string()).default({}).nullable().optional(),
});

export type ExecuteFunctionsType = z.infer<typeof executeFunctionsSchema>;

// ============================================
// Import Chatbot Schema
// ============================================

export const importChatbotSchema = z.object({
  fileData: z.string().min(1, 'File data is required'),
  encryptionKey: z.string().min(1, 'Encryption key is required'),
  newName: z.string().optional(),
});

export type ImportChatbotSchemaType = z.infer<typeof importChatbotSchema>;

// ============================================
// Chatbot File Schema (for Lambda Code Editor)
// ============================================

/**
 * Schema for chatbot JSON files in Lambda Studio code-editor.
 * Used to validate chatbot data before saving to ensure sync works on deploy.
 * Uses .strict() to reject unknown/extra fields (OpenAI might add invalid keys).
 */
export const chatbotFileSchema = z
  .object({
    // Required fields - id will be auto-generated if missing
    id: z.string().min(1, 'Chatbot ID is required'),
    name: z.string().min(1, 'Chatbot name is required'),

    // Optional fields with defaults.
    // Every optional field is ALSO .nullable(): the chatbot is serialized straight
    // from its DB row, where an unset column is `null` (not absent). Without
    // .nullable() the strict schema rejects a real bot with "Expected X, received
    // null". null is accepted as-is; only `undefined` triggers a .default().
    description: z.string().nullable().optional().default(''),
    model: z.string().nullable().optional().default('google/gemini-3.5-flash'),
    // No default on purpose: a missing prompt must stay missing in the zip
    // so the deploy sync preserves the bot's existing DB prompt instead of
    // overwriting it with a placeholder.
    systemPrompt: z.string().nullable().optional(),
    hasTimeout: z.boolean().nullable().optional().default(false),
    timeoutDuration: z.number().nullable().optional().default(null),
    contextLength: z.number().nullable().optional().default(100),
    temperature: z.number().nullable().optional(),
    maxTokens: z.number().nullable().optional(),
    reasoningEffort: chatbotReasoningEffortSchema.nullable().optional(),
    verbosity: chatbotVerbositySchema.nullable().optional(),

    // Send message type options
    sendMsgType: z.array(z.string()).nullable().optional(),
    truncateMsg: z.boolean().nullable().optional(),
    showTypingIndicator: z.boolean().nullable().optional(),
    sendFirstMessage: z.boolean().nullable().optional(),
    minBotMsgDelay: z.number().nullable().optional(),

    // Follow-up/nudge settings
    maxConsecutiveNudges: z.number().nullable().optional(),
    maxBotMessagesPerSession: z.number().nullable().optional(),
    followUpPrompt: z.string().nullable().optional(),
    followUpLocation: z.enum(['system', 'assistant']).nullable().optional(),
    enableFollowUpType: z.boolean().nullable().optional(),

    // Timestamp in context
    includeTimestampInContext: z.boolean().nullable().optional(),
    contextTimestampTimezone: z.string().nullable().optional(),

    // Voice bot config
    voiceBot: voiceBotSchema.nullable().optional(),

    // Function references (linked functions - used in Lambda code)
    // Accepts both old string[] and new { name, directReturn }[] formats
    functions: z
      .array(
        z.union([
          z.string(),
          z.object({
            name: z.string(),
            directReturn: z.boolean().default(false),
          }),
        ]),
      )
      .nullable()
      .optional(),

    // Metadata
    createdBy: z
      .enum(['copilot', 'human', 'api'])
      .nullable()
      .optional()
      .default('copilot'),
    syncedFromLambdaVersion: z.string().nullable().optional(),
    createdAt: z.string().nullable().optional(),
    updatedAt: z.string().nullable().optional(),

    // The remaining fields the deploy-sync maps — listed so .strict() (below) does
    // NOT reject a real bot. Complex objects stay loose (z.record) since the backend
    // re-validates them; we only need the KEY allowed so a typo isn't laundered in.
    type: chatbotTypeSchema.nullable().optional(),
    enableReply: z.boolean().nullable().optional(),
    imageContextFilter: z
      .enum(['all', 'incoming', 'outgoing'])
      .nullable()
      .optional(),
    latestImagesCount: z.number().nullable().optional(),
    audioTranscriptionConfig: z.record(z.unknown()).nullable().optional(),
    resultConfig: z.record(z.unknown()).nullable().optional(),
    builtInTools: z.array(z.unknown()).nullable().optional(),
    sessionExpiryHours: z.number().nullable().optional(),
    // Spread verbatim into the file by chatbotToLambdaJson (DB-only fields are
    // stripped, these survive in `...rest`). Loose like the other complex objects —
    // the key just has to be allowed so .strict() doesn't reject a real bot.
    flowDefinition: z.record(z.unknown()).nullable().optional(),
    linkedFunctions: z.array(z.unknown()).nullable().optional(),
    // Journey columns on the same chatbot row — null on a conversational bot, but
    // they ride along in `...rest`, so the keys must be allowed here too.
    triggerEvents: z.array(z.string()).nullable().optional(),
    triggerEventsByBusiness: z
      .record(z.array(z.string()))
      .nullable()
      .optional(),
    // Meta Business Agent column (type 'meta_ai') — rides along the same way.
    metaAgentConfig: metaAgentConfigSchema.nullable().optional(),
  })
  // STRICT: a misspelled/unknown key (e.g. `modell` for `model`) fails loudly at
  // save instead of being silently stripped. Keep the field list above complete.
  .strict();

export type ChatbotFileType = z.infer<typeof chatbotFileSchema>;

const isValidUuid = (v: unknown): v is string =>
  z.string().uuid().safeParse(v).success;
const ensureUuid = (id: unknown): string =>
  isValidUuid(id) ? id : crypto.randomUUID();

/**
 * Prepares chatbot data for saving:
 * - Generates a UUID when id is missing, blank, or not a valid UUID
 * - Validates and strips unknown keys (Zod does this automatically)
 * - Applies default values
 */
export const prepareChatbotForSave = (
  data: unknown,
):
  | { success: true; data: ChatbotFileType }
  | { success: false; error: z.ZodError } => {
  const rawData = data as Record<string, unknown>;

  const dataWithId = {
    ...rawData,
    id: ensureUuid(rawData.id),
  };

  // Validate - Zod automatically strips unknown keys
  const result = chatbotFileSchema.safeParse(dataWithId);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
};

/**
 * Formats Zod validation errors into human-readable messages
 */
export const formatValidationErrors = (error: z.ZodError): string[] => {
  return error.errors.map(err => {
    const path = err.path.join('.');
    return path ? `${path}: ${err.message}` : err.message;
  });
};

// ============================================
// Static Flowbot settings file schema
// ============================================

/**
 * Settings file for a static flowbot (`/static_flowbot/<name>.json`, nestable).
 *
 * A static flowbot persists into the SAME `chatbot` table (type 'static_flowbot') as
 * LLM/flow bots, so its settings are a SUBSET of the chatbot config — reused from
 * `chatbotFileSchema` here so those shared columns have ONE definition. It stays its
 * own schema only because the FILE shape differs: a pyflow `.json` is settings ONLY
 * (no `name` — the file path is the name; no `systemPrompt`/`model` — the bot's logic
 * lives in the `.py`). STRICT so a typo'd setting surfaces before deploy.
 */
export const pyFlowSettingsSchema = chatbotFileSchema
  .pick({
    showTypingIndicator: true,
    truncateMsg: true,
    minBotMsgDelay: true,
    maxBotMessagesPerSession: true,
    sendFirstMessage: true,
  })
  .extend({
    // The file path is the bot's identity; an explicit id only pins it across renames.
    id: z.string().optional(),
    // pyflow-only: written straight to the chatbot's sessionExpiryHours column on sync.
    sessionExpiryHours: z.number().positive().optional(),
  })
  .strict();

export type PyFlowSettingsType = z.infer<typeof pyFlowSettingsSchema>;

/**
 * Like `prepareChatbotForSave`, for a python flow bot's settings file: generate a
 * stable `id` (UUID) when one is missing — exactly as a chatbot does — so the deploy
 * can pin the bot across renames, then validate strictly. The caller writes the
 * filled id back into the file.
 */
export const preparePyFlowSettingsForSave = (
  data: unknown,
):
  | { success: true; data: PyFlowSettingsType }
  | { success: false; error: z.ZodError } => {
  // A non-object (array / number / string / null) must fail loudly — NOT be spread
  // into a valid empty `{}` settings object. Hand it straight to the schema so it
  // rejects with "expected object".
  const raw =
    data !== null && typeof data === 'object' && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : null;
  const candidate = raw === null ? data : { ...raw, id: ensureUuid(raw.id) };
  const result = pyFlowSettingsSchema.safeParse(candidate);
  return result.success
    ? { success: true, data: result.data }
    : { success: false, error: result.error };
};

// ============================================
// Journey bot settings file schema
// ============================================

/**
 * Settings file for a journey bot (`/journeybot/<name>.json`, nestable).
 *
 * A journey persists into the SAME `chatbot` table (type 'journey') as LLM/flow
 * bots. The FILE shape is settings ONLY (no `name` — the file path is the name;
 * no `systemPrompt`/`model` — the logic lives in the `.py`). `triggerEvents` is
 * REQUIRED: it is what the events ingress fans out on; a journey without it can
 * never start. STRICT so a typo'd setting surfaces before deploy.
 */
export const journeySettingsSchema = chatbotFileSchema
  // A journey is a SUPERSET of the python flow bot, so it carries the same
  // conversational-bot columns (typing/truncate/delay/session-cap/greeting) —
  // reused from chatbotFileSchema so those columns have ONE definition.
  .pick({
    showTypingIndicator: true,
    truncateMsg: true,
    minBotMsgDelay: true,
    maxBotMessagesPerSession: true,
    sendFirstMessage: true,
  })
  .extend({
    // The file path is the bot's identity; an explicit id only pins it across renames.
    id: z.string().optional(),
    triggerEvents: z.array(z.string().min(1)).nonempty(),
    // Per-business override of triggerEvents (key = businessId as string).
    // [] mutes that business entirely; an absent key means the default list.
    triggerEventsByBusiness: z.record(z.array(z.string().min(1))).optional(),
    // Idle-run expiry; written straight to the chatbot's sessionExpiryHours column
    // on sync. Waits (wakeSeconds) extend the run past this automatically.
    sessionExpiryHours: z.number().positive().optional(),
  })
  .strict();

export type JourneySettingsType = z.infer<typeof journeySettingsSchema>;

/**
 * Like `prepareChatbotForSave`, for a journey's settings file: generate a
 * stable `id` (UUID) when one is missing — exactly as a chatbot does — so the
 * deploy can pin the bot across renames, then validate strictly. The caller
 * writes the filled id back into the file.
 */
export const prepareJourneySettingsForSave = (
  data: unknown,
):
  | { success: true; data: JourneySettingsType }
  | { success: false; error: z.ZodError } => {
  // A non-object (array / number / string / null) must fail loudly — NOT be spread
  // into a valid empty `{}` settings object. Hand it straight to the schema so it
  // rejects with "expected object".
  const raw =
    data !== null && typeof data === 'object' && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : null;
  const candidate = raw === null ? data : { ...raw, id: ensureUuid(raw.id) };
  const result = journeySettingsSchema.safeParse(candidate);
  return result.success
    ? { success: true, data: result.data }
    : { success: false, error: result.error };
};

// ============================================
// Workspace save-time validation
// ============================================

const isChatbotJsonFile = (path: string): boolean =>
  path.startsWith('/chatbot/') && path.endsWith('.json');

const isMetaAgentJsonPath = (path: string): boolean =>
  path.startsWith('/meta_ai_agent/') && path.endsWith('.json');

// chatbot, python flow bot, journey bot, and flow bot all upsert into the
// chatbot table by id
const isBotConfigPath = (path: string): boolean =>
  isChatbotJsonFile(path) ||
  isPyFlowConfigPath(path) ||
  isJourneyConfigPath(path) ||
  isFlowBotJsonPath(path) ||
  isMetaAgentJsonPath(path);

// meta_ai_agent/<name>.json = { id?, name } + MetaAgentConfig. Same save-time
// contract as chatbots: schema-clean + id-filled, so the deploy extractor
// (which requires id+name) never has to skip a file the editor accepted.
const metaAgentFileSchema = metaAgentConfigSchema.extend({
  id: z.string().nullable().optional(),
  name: z.string().min(1),
});

export const prepareMetaAgentForSave = (
  data: unknown,
):
  | { success: true; data: Record<string, unknown> }
  | { success: false; error: z.ZodError } => {
  const result = metaAgentFileSchema.safeParse(data);
  return result.success
    ? {
        success: true,
        data: { ...result.data, id: ensureUuid(result.data.id) },
      }
    : { success: false, error: result.error };
};

/**
 * Validate every chatbot + python-flow-bot + journey-bot JSON in a Lambda workspace BEFORE
 * save/deploy, so only schema-clean data reaches the backend sync — at every save
 * path (code editor, copilot save_code, playground, flow builder), not just one.
 * Nested folders are validated too (prefix match). Returns the validated files
 * (chatbot JSON cleaned + id-filled via prepareChatbotForSave) and a list of
 * human-readable `path: message` errors; callers BLOCK the save when it is non-empty.
 */
export const validateWorkspaceFilesForSave = (
  files: Record<string, string>,
): { files: Record<string, string>; errors: string[] } => {
  const out = { ...files };
  const errors: string[] = [];

  for (const [path, content] of Object.entries(files)) {
    if (!content) continue;
    const chatbot = isChatbotJsonFile(path);
    const pyFlow = isPyFlowConfigPath(path);
    const journey = isJourneyConfigPath(path);
    const flowBot = isFlowBotJsonPath(path);
    const metaAgent = isMetaAgentJsonPath(path);
    if (!chatbot && !pyFlow && !journey && !flowBot && !metaAgent) continue;

    let parsed: unknown;
    try {
      parsed = JSON.parse(content);
    } catch {
      errors.push(`${path}: Invalid JSON syntax`);
      continue;
    }

    if (flowBot) {
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const raw = parsed as Record<string, unknown>;
        out[path] = JSON.stringify({ ...raw, id: ensureUuid(raw.id) }, null, 2);
      }
      continue;
    }

    const result = chatbot
      ? prepareChatbotForSave(parsed)
      : metaAgent
        ? prepareMetaAgentForSave(parsed)
        : pyFlow
          ? preparePyFlowSettingsForSave(parsed)
          : prepareJourneySettingsForSave(parsed);
    if (result.success) out[path] = JSON.stringify(result.data, null, 2);
    else
      errors.push(
        `${path}: ${formatValidationErrors(result.error).join(', ')}`,
      );
  }

  const idToPaths = new Map<string, string[]>();
  for (const [path, content] of Object.entries(out)) {
    if (!isBotConfigPath(path)) continue;
    let id: unknown;
    try {
      ({ id } = JSON.parse(content) as { id?: unknown });
    } catch {
      continue;
    }
    if (typeof id !== 'string' || !id) continue;
    idToPaths.set(id, [...(idToPaths.get(id) ?? []), path]);
  }
  for (const [id, paths] of idToPaths) {
    if (paths.length > 1)
      errors.push(
        `Duplicate bot id "${id}" in ${paths
          .sort()
          .join(', ')} — give each bot its own unique id`,
      );
  }

  return { files: out, errors };
};
